/**
 * A single-choice filter with a type-to-search list — the Employee and Supervisor
 * filters, Shayna 2026-10-05 (#65): "apply this type of filter-search to employee and
 * supervisor filters", pointing at the Job title control (MultiSelect).
 *
 * So it is MultiSelect's look, one pick instead of many: a closed control that reads
 * "All employees" or the name picked, opening to a search box over the list. A native
 * <select> of ~750 names could only be scrolled, or typed at blind from the first
 * letter of a surname.
 *
 * Keyboard: on the closed control, ↓ or Enter opens it and any letter opens it already
 * searching. In the list, type to narrow, ↑/↓ to move, Enter to pick, Esc to close
 * (and only that — an enlarged table around it stays open).
 *
 * Matching is by words in any order, so "sarah barr", "barr sarah" and "barr, s" all
 * find "BARR, Sarah": names are stored "Last, First" and nobody types them that way.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { MODELED_FILTER_NOTE } from './MultiSelect'
import { matches } from '@/ui/match'
import type { Definition } from '@/measures/definitions'
import { Info } from './Info'

export interface PickOption {
  value: string
  label: string
  /**
   * A second thing the option is known by, shown faint after the label and searched
   * with it: the Employee filter's Paylocity ID (review #373), so typing the ID finds
   * the person as their name does.
   */
  detail?: string | null
}

const control: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 7px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  minWidth: 0,
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
}

// Re-exported: the filter dropdowns and the header's search share one match.
export { matches }

export function SearchPick({
  label,
  value,
  options,
  onChange,
  allValue,
  allLabel,
  placeholder = 'Pick one',
  lead = false,
  modeled = false,
  hint,
  title,
  help,
  minWidth,
  custom,
  quietLabel = false,
}: {
  label: string
  value: string
  options: PickOption[]
  onChange: (v: string) => void
  /** The "everyone" choice, listed first. Omit for a picker that must name someone. */
  allValue?: string
  allLabel?: string
  /** The closed control's text when nothing is picked and there is no "all". */
  placeholder?: string
  /** The page's primary control: label in accent ink, as Select's `lead`. */
  lead?: boolean
  modeled?: boolean
  hint?: string
  title?: string
  /** What the control picks, as the "i" beside its name. */
  help?: Definition
  /** For a flex row of filters, where 100% of nothing is nothing. */
  minWidth?: number
  /**
   * A typed value that is not in the list, offered as a choice of its own — or null
   * when the query is not one. View as uses it for an email with no grant row.
   */
  custom?: (query: string) => PickOption | null
  /**
   * The label for screen readers only, where the words around the control already say
   * what it is (the Rules page's sentences: "When the payer type is [ ▾ ]").
   */
  quietLabel?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  /**
   * Where the list opens and how wide it may grow. It hangs off the control's left edge,
   * or its right edge when that leaves more room — the right-hand control on a phone —
   * so a name is neither cut short nor run off the screen.
   */
  const [place, setPlace] = useState<{ side: 'left' | 'right'; room: number }>({
    side: 'left',
    room: 340,
  })
  const box = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const id = useId()

  const hasAll = allValue !== undefined
  const isAll = hasAll && value === allValue
  const current = options.find((o) => o.value === value)?.label

  const shown = useMemo(() => {
    const typed = custom?.(q) ?? null
    const hits = [
      ...options.filter((o) => matches(o.detail ? `${o.label} ${o.detail}` : o.label, q)),
      ...(typed && !options.some((o) => o.value === typed.value) ? [typed] : []),
    ]
    // "All" stays on top while browsing; once the reader is searching it is noise.
    return hasAll && !q.trim() ? [{ value: allValue, label: allLabel ?? 'All' }, ...hits] : hits
  }, [options, q, hasAll, allValue, allLabel, custom])

  // Opening starts on the current pick, so ↓ moves on from where the reader already is.
  const openList = (seed = '') => {
    const r = trigger.current?.getBoundingClientRect()
    if (r) {
      const rightward = window.innerWidth - r.left - 8
      const leftward = r.right - 8
      const side = rightward >= 260 || rightward >= leftward ? 'left' : 'right'
      setPlace({ side, room: Math.max(r.width, Math.min(340, side === 'left' ? rightward : leftward)) })
    }
    setQ(seed)
    setOpen(true)
    const at = seed ? 0 : (hasAll ? 1 : 0) + options.findIndex((o) => o.value === value)
    setActive(isAll || at < 0 ? 0 : at)
  }
  const close = (refocus: boolean) => {
    setOpen(false)
    setQ('')
    if (refocus) trigger.current?.focus()
  }
  const pick = (v: string) => {
    if (v !== value) onChange(v)
    close(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (open) list.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [active, open, q])

  return (
    <div
      ref={box}
      style={{ display: 'grid', gap: 4, position: 'relative', minWidth }}
      title={title}
    >
      <span
        className={quietLabel ? 'eyebrow sr-only' : 'eyebrow'}
        id={`${id}-label`}
        style={lead ? { color: 'var(--accent-ink)' } : undefined}
      >
        {label}
        {hint && <span style={{ color: 'var(--ink-3)' }}> {hint}</span>}
        <Info def={help} />
        {modeled && (
          <span className="modeled" style={{ marginLeft: 6, verticalAlign: 0 }} title={MODELED_FILTER_NOTE}>
            M
          </span>
        )}
      </span>

      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-label ${id}-value`}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={(e) => {
          if (open) return
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            openList()
          } else if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
            // A letter on the closed control starts the search with it.
            e.preventDefault()
            openList(e.key)
          }
        }}
        style={{
          ...control,
          borderColor: isAll || (!hasAll && !current) ? 'var(--rule-2)' : 'var(--accent)',
        }}
      >
        <span
          id={`${id}-value`}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isAll || !current ? 'var(--ink-3)' : 'var(--ink)',
          }}
        >
          {isAll ? (allLabel ?? 'All') : (current ?? placeholder)}
        </span>
        <span className="mono" style={{ marginLeft: 'auto', color: 'var(--ink-3)' }} aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            ...(place.side === 'left' ? { left: 0 } : { right: 0 }),
            zIndex: 30,
            minWidth: '100%',
            width: 'max-content',
            maxWidth: place.room,
            marginTop: 4,
            background: 'var(--surface)',
            border: '1px solid var(--rule-2)',
            borderRadius: 7,
            boxShadow: 'var(--shadow)',
            padding: 6,
          }}
        >
          <input
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={shown[active] ? `${id}-o${active}` : undefined}
            aria-label={`Search ${label}`}
            autoComplete="off"
            spellCheck={false}
            value={q}
            placeholder="Search"
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 1, shown.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 1, 0))
              } else if (e.key === 'PageDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 10, shown.length - 1))
              } else if (e.key === 'PageUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 10, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const o = shown[active]
                if (o) pick(o.value)
              } else if (e.key === 'Escape') {
                // Handled here, so an enlarged table (PopOut) around it stays open.
                e.preventDefault()
                e.stopPropagation()
                close(true)
              } else if (e.key === 'Tab') {
                close(false)
              }
            }}
            style={{ ...control, cursor: 'text', display: 'block', marginBottom: 6 }}
          />

          <ul
            id={`${id}-list`}
            ref={list}
            role="listbox"
            aria-label={label}
            style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 280, overflowY: 'auto' }}
          >
            {shown.length === 0 && (
              <li style={{ fontSize: 'var(--t-table)', color: 'var(--ink-3)', padding: '6px 5px' }}>
                Nothing matches “{q}”.
              </li>
            )}
            {shown.map((o, i) => {
              const picked = o.value === value
              const isAllRow = hasAll && o.value === allValue
              return (
                <li
                  key={isAllRow ? '\u0000all' : o.value}
                  id={`${id}-o${i}`}
                  role="option"
                  aria-selected={picked}
                  // mousedown, not click: the search box's blur would come first.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(o.value)
                  }}
                  onMouseMove={() => i !== active && setActive(i)}
                  style={{
                    display: 'flex',
                    gap: 7,
                    alignItems: 'center',
                    padding: '4px 5px',
                    borderRadius: 5,
                    cursor: 'pointer',
                    fontSize: 'var(--t-table)',
                    background: i === active ? 'var(--accent-soft)' : undefined,
                    color: i === active ? 'var(--accent-ink)' : 'var(--ink)',
                    fontWeight: picked ? 600 : undefined,
                    borderBottom: isAllRow ? '1px solid var(--rule)' : undefined,
                    marginBottom: isAllRow ? 4 : undefined,
                  }}
                >
                  <span aria-hidden="true" style={{ width: 10, flex: 'none', color: 'var(--accent)' }}>
                    {picked ? '✓' : ''}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.label}
                    {o.detail && (
                      <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 'var(--t-micro)' }}>
                        {' '}
                        {o.detail}
                      </span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>

          {q.trim() && shown.length > 0 && (
            <p className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', margin: '6px 5px 0' }}>
              {shown.length} of {options.length}
            </p>
          )}
          {hasAll && !isAll && (
            <button
              type="button"
              onClick={() => pick(allValue)}
              style={{ ...control, marginTop: 6, justifyContent: 'center', color: 'var(--accent-ink)' }}
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  )
}
